import type { ReactNode } from "react"
import { useState } from "react"

import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"
import {
  Building2,
  Calendar,
  DollarSign,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Plug,
  Route,
  Settings,
  Users,
  X,
  XCircle,
  Zap,
} from "lucide-react"

import { Button } from "@/components/ui/button"

import { useAuth } from "../context/auth-context"

interface LayoutProps {
  children: ReactNode
}

interface NavItem {
  path: string
  label: string
  icon: LucideIcon
  roles: string[]
}

const Layout = ({ children }: LayoutProps) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const userRoleLabel = user?.role?.replace(/_/g, " ")

  const handleLogout = () => {
    logout()
    navigate({ to: "/auth" })
  }

  const isActive = (path: string) => location.pathname === path

  const navigationItems: NavItem[] = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      roles: ["all"],
    },
    { path: "/stations", label: "Stations", icon: MapPin, roles: ["customer"] },
    {
      path: "/appointments",
      label: "Appointments",
      icon: Calendar,
      roles: ["all"],
    },
    {
      path: "/trip-planner",
      label: "Trip planner",
      icon: Route,
      roles: ["customer"],
    },
    {
      path: "/users",
      label: "Users",
      icon: Users,
      roles: ["super_admin", "company_admin"],
    },
    {
      path: "/companies",
      label: "Companies",
      icon: Building2,
      roles: ["super_admin"],
    },
    {
      path: "/branches",
      label: "Branches",
      icon: MapPin,
      roles: ["super_admin", "company_admin"],
    },
    {
      path: "/pricing",
      label: "Pricing",
      icon: DollarSign,
      roles: ["super_admin", "company_admin", "branch_admin"],
    },
    {
      path: "/chargers",
      label: "Chargers",
      icon: Plug,
      roles: ["super_admin", "company_admin", "branch_admin"],
    },
    {
      path: "/closures",
      label: "Station Closures",
      icon: XCircle,
      roles: ["super_admin", "company_admin", "branch_admin"],
    },
    { path: "/settings", label: "Settings", icon: Settings, roles: ["all"] },
  ]

  const visibleItems = navigationItems.filter(
    (item) =>
      item.roles.includes("all") ||
      (user?.role && item.roles.includes(user.role))
  )

  return (
    <div className="layout-shell">
      <div className="lg:flex">
        <aside className="hidden h-screen w-[280px] shrink-0 flex-col border-r border-black/10 bg-white lg:sticky lg:top-0 lg:flex">
          <div className="px-6 pt-6">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 no-underline"
              onClick={() => setSidebarOpen(false)}
            >
              <div className="layout-badge flex size-11 items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[18px] font-bold text-black">VoltFlow</p>
                <p className="text-sm text-[#4b4b4b]">Operations workspace</p>
              </div>
            </Link>
          </div>

          <div className="px-6 pt-6">
            <div className="rounded-[12px] bg-[#f3f3f3] p-4">
              <p className="text-sm font-semibold text-black">{user?.name}</p>
              <p className="mt-1 text-sm text-[#4b4b4b]">{user?.email}</p>
              <p className="mt-3 text-xs font-medium capitalize text-[#4b4b4b]">
                {userRoleLabel}
              </p>
            </div>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
            {visibleItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-full px-4 py-3 text-[15px] font-medium no-underline transition-colors ${
                    active
                      ? "bg-black text-white"
                      : "text-black hover:bg-[#efefef]"
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="p-4 pt-0">
            <Button
              variant="secondary"
              className="w-full justify-center"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-40 border-b border-black/10 bg-white/95 backdrop-blur lg:hidden">
            <div className="flex h-16 items-center gap-3 px-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-3 no-underline"
                onClick={() => setSidebarOpen(false)}
              >
                <div className="layout-badge flex size-10 items-center justify-center">
                  <Zap className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-base font-bold text-black">VoltFlow</p>
                  <p className="text-xs text-[#4b4b4b]">Operations workspace</p>
                </div>
              </Link>

              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
                className="ml-auto"
              >
                {sidebarOpen ? <X /> : <Menu />}
              </Button>
            </div>
          </header>

          {sidebarOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
              <div className="fixed inset-y-4 left-4 z-50 w-[min(86vw,320px)] rounded-[12px] bg-white p-4 shadow-[rgba(0,0,0,0.16)_0px_4px_16px_0px] lg:hidden">
                <div className="border-b border-black/10 pb-4">
                  <p className="text-sm font-semibold text-black">{user?.name}</p>
                  <p className="mt-1 text-sm text-[#4b4b4b]">{user?.email}</p>
                  <p className="mt-3 text-xs font-medium capitalize text-[#4b4b4b]">
                    {userRoleLabel}
                  </p>
                </div>

                <nav className="space-y-1 py-4">
                  {visibleItems.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.path)
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center justify-between rounded-full px-4 py-3 text-sm font-medium no-underline transition-colors ${
                          active
                            ? "bg-black text-white"
                            : "text-black hover:bg-[#efefef]"
                        }`}
                        data-testid={`nav-${item.path.slice(1)}`}
                      >
                        <span>{item.label}</span>
                        <Icon className="h-4 w-4" />
                      </Link>
                    )
                  })}
                </nav>

                <Button
                  variant="secondary"
                  className="mt-2 w-full justify-center"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </Button>
              </div>
            </>
          )}

          <main className="px-4 py-6 lg:px-10 lg:py-8">
            <div className="mx-auto max-w-[1136px]">{children}</div>
          </main>
        </div>
      </div>
    </div>
  )
}

export default Layout
